import { createFileRoute, Link, notFound, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { Reveal } from "@/components/Reveal";
import { MapPin } from "lucide-react";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import { Skeleton } from "@/components/ui/skeleton";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { INSURANCES, SPECIALTIES } from "@/data/heallexa";
import { citiesInState, US_CITIES_VALID } from "@/data/us-cities";
import {
  SEO_INSURANCE_IDS,
  specialtySlug,
  stateFromNameSlug,
  stateNameSlug,
  toSlug,
} from "@/lib/seo-slugs";
import { coordParam, useNearMe } from "@/lib/use-near-me";
import { runProviderSearch, cleanQueryText, type SearchLoaderResult } from "@/lib/search-loader";
import { SearchExperience, type ResolvedCity } from "@/components/search/SearchExperience";
import { providersRouteFor, resolveCityFromLabel } from "@/lib/providers-nav";

const SITE = "https://heallexa.com";

// Mirrors providers.$id.tsx's specialty branch — city is this route's own
// identity (baked into the path), specialty rides along as an optional
// query param. Kept as its own route (2 segments, no collision risk).
type ProviderCitySearch = { specialty?: string; insurance?: string; q?: string; radius?: number; page?: number; lat?: number; lng?: number };

type StateInfo = { code: string; name: string };
type CityInfo = { name: string; state: string };

function CityPageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Skeleton className="h-4 w-48 mb-6" />
        <div className="mb-10">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-12 w-2/3 mb-4" />
          <Skeleton className="h-4 w-full max-w-lg mb-6" />
          <div className="flex gap-3">
            <Skeleton className="h-10 w-44" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card-surface-flat rounded-md p-4 flex gap-4">
              <Skeleton className="h-16 w-16 shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export const Route = createFileRoute("/providers/$state/$city")({
  // Optional-key search schema (every field `?:`) — see providers.$id.tsx
  // for why this form specifically avoids forcing a `search` prop on every
  // caller once a route declares validateSearch.
  validateSearch: (s: Record<string, unknown>): ProviderCitySearch => ({
    specialty: cleanQueryText(s.specialty),
    insurance: cleanQueryText(s.insurance),
    q: cleanQueryText(s.q),
    radius: typeof s.radius === "number" ? s.radius : (typeof s.radius === "string" ? Number(s.radius) || undefined : undefined),
    page: typeof s.page === "number" ? s.page : (typeof s.page === "string" ? Number(s.page) || undefined : undefined),
    lat: coordParam(s.lat, -90, 90),
    lng: coordParam(s.lng, -180, 180),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ params, deps }) => {
    await setSeoPageCache();
    const state = stateFromNameSlug(params.state);
    if (!state) throw notFound();
    const citySlug = params.city.toLowerCase();
    const city = US_CITIES_VALID.find(
      (c) => c.state === state.code && toSlug(c.name) === citySlug,
    );
    if (!city) throw notFound();
    const searchResult = await runProviderSearch({ ...deps, location: `${city.name}, ${state.code}`, distanceOnly: true });
    return { state, city, searchResult };
  },
  pendingMs: 100,
  pendingMinMs: 300,
  pendingComponent: CityPageSkeleton,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { state, city } = loaderData;
    const title = `Healthcare Providers in ${city.name}, ${state.code} | Heallexa`;
    const description = `Find healthcare providers in ${city.name}, ${state.code}. Search doctors, dentists, therapists, chiropractors, specialists, and clinics near you.`;
    const stateSlug = stateNameSlug(state.name);
    const url = `${SITE}/providers/${stateSlug}/${toSlug(city.name)}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Providers", url: "/browse" },
            { name: state.name, url: `/providers/${stateSlug}` },
            { name: city.name, url: `/providers/${stateSlug}/${toSlug(city.name)}` },
          ]),
        ),
        jsonLdScript(
          faqPageSchema([
            {
              q: `How do I find a doctor in ${city.name}, ${state.code}?`,
              a: `Use Heallexa to browse ${city.name} providers by specialty and insurance, then contact the office directly.`,
            },
            {
              q: `Which insurance plans do ${city.name} providers accept?`,
              a: `Most ${city.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare.`,
            },
            {
              q: `Is Heallexa free for patients?`,
              a: `Yes. Search, browse, and contact providers in ${city.name} for free, no signup required.`,
            },
          ]),
        ),
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-2xl font-semibold mb-2">City not found</h1>
        <Link to="/browse" className="text-primary underline">
          Browse all providers
        </Link>
      </div>
    </div>
  ),
});

function CityHubContent({ state, city }: { state: StateInfo; city: CityInfo }) {
  const stateSlug = stateNameSlug(state.name);
  const cs = `${toSlug(city.name)}-${state.code.toLowerCase()}`;
  const topSpecialties = SPECIALTIES.slice(0, 16);
  const nearby = citiesInState(state.code).filter((c) => c.name !== city.name).slice(0, 12);

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <div className="mb-10">
        <Link
          to="/list-your-practice"
          className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors rounded-md"
        >
          List your practice in {city.name}
        </Link>
      </div>

      <Reveal from="up" delay={60}>
        <SeoLinkList
          heading={`Top specialties in ${city.name}`}
          links={topSpecialties.map((s) => ({
            label: `${s} in ${city.name}`,
            href: `/specialty/${specialtySlug(s)}/${cs}`,
          }))}
          columns={3}
          className="mb-10"
        />
      </Reveal>

      <Reveal from="up" delay={60}>
        <SeoLinkList
          heading={`Insurance accepted in ${city.name}`}
          links={SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id))
            .filter(Boolean)
            .map((ins) => ({
              label: `Doctors in ${city.name} accepting ${ins!.short}`,
              href: `/insurance/${ins!.id}/${cs}`,
            }))}
          columns={3}
          className="mb-10"
        />
      </Reveal>

      {nearby.length > 0 && (
        <Reveal from="up" delay={60}>
          <SeoLinkList
            heading={`Nearby cities in ${state.name}`}
            links={nearby.map((c) => ({
              label: c.name,
              href: `/providers/${stateSlug}/${toSlug(c.name)}`,
            }))}
            columns={3}
            icon={MapPin}
            className="mb-10"
          />
        </Reveal>
      )}

      <SeoFaq
        className="mb-8"
        items={[
          {
            q: `How do I find a doctor in ${city.name}?`,
            a: `Use Heallexa to filter ${city.name} providers by specialty and insurance, then contact the office directly.`,
          },
          {
            q: `Are these ${city.name} doctors accepting new patients?`,
            a: `Most listings indicate whether the provider is accepting new patients. Always confirm with the office.`,
          },
          {
            q: `Which insurance plans do ${city.name} doctors accept?`,
            a: `Most accept major plans like BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare. Use the filter above to narrow down.`,
          },
          {
            q: `Is Heallexa free for patients?`,
            a: `Yes. Search, browse, and contact ${city.name} providers for free, no signup required.`,
          },
        ]}
      />
    </div>
  );
}

function CityPage() {
  const { state, city, searchResult }: { state: StateInfo; city: CityInfo; searchResult: SearchLoaderResult } = Route.useLoaderData();
  const search = useSearch({ from: "/providers/$state/$city" });
  const navigate = useNavigate({ from: "/providers/$state/$city" });
  const isRouteLoading = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId === "/providers/$state/$city" && m.status === "pending"),
  });

  const onQueryChange = useCallback((q: string) => {
    navigate({ search: (prev: any) => ({ ...prev, q: q || undefined, page: undefined }), replace: true });
  }, [navigate]);

  // Committing a DIFFERENT city always navigates to that city's own page —
  // carrying the active specialty forward onto the continuous 3-segment URL
  // when one is set. Unresolved typed text has no clean home on this route,
  // so it falls back to /search rather than dead-ending.
  const goToCity = useCallback((cityLabel: string, matched: ResolvedCity | null, currentSpecialty: string) => {
    const resolved = matched ?? resolveCityFromLabel(cityLabel);
    const dest = resolved ? providersRouteFor(currentSpecialty === "any" ? undefined : currentSpecialty, resolved) : null;
    if (dest) {
      navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
      return;
    }
    navigate({
      to: "/search",
      search: { location: cityLabel || undefined, specialty: currentSpecialty === "any" ? undefined : currentSpecialty },
    });
  }, [navigate]);

  // Picking a specialty here lands on the continuous /providers/{state}/
  // {city}/{specialty} URL instead of staying + adding ?specialty=.
  const onSpecialtyCommit = useCallback((specialty: string) => {
    const dest = providersRouteFor(specialty, { city: city.name, state: state.code });
    if (dest) navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
  }, [navigate, city.name, state.code]);

  const onRadiusChange = useCallback((radius: number) => {
    navigate({ search: (prev: any) => ({ ...prev, radius: radius > 0 ? radius : undefined, page: undefined }), replace: true });
  }, [navigate]);

  const onPageChange = useCallback((page: number) => {
    navigate({ search: (prev: any) => ({ ...prev, page: page === 1 ? undefined : page }), replace: false });
  }, [navigate]);

  // Distance-only geolocation: this page IS "the city in the URL", so the visitor's
  // coordinates must not move the search centre — they only stamp each card
  // with how far it is and let the sort default to nearest-first. See useNearMe.
  const { useMyLocation } = useNearMe({ navigate, lat: search.lat, lng: search.lng });

  return (
    <SearchExperience
      loaderData={searchResult}
      initial={{ q: search.q ?? "", location: `${city.name}, ${state.code}`, radius: search.radius ?? 0 }}
      isRouteLoading={isRouteLoading}
      onQueryChange={onQueryChange}
      onLocationSubmit={(label) => goToCity(label, null, search.specialty ?? "any")}
      onSpecialtyCommit={onSpecialtyCommit}
      onCityCommit={goToCity}
      onRadiusChange={onRadiusChange}
      onPageChange={onPageChange}
      onUseMyLocation={useMyLocation}
      belowResults={<CityHubContent state={state} city={city} />}
    />
  );
}
